import { useState, useEffect } from "react";
import NumericFormat from "react-number-format";
import RequestConfig from "../mini_components/RequestConfig";
import { convertToJalali } from "../mini_components/ConvertToJalali";
import { Link, useNavigate } from "react-router-dom";
import { calculateWithdrawStaleTime } from "../utils/utils";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

// Status-based color mapping
const statusColors = {
  PAID: "bg-success-10 text-success-90",
  CANCELED: "bg-error-10 text-error-90",
  PENDING: "bg-secondary-10 text-secondary-60",
};

const getStatusStyle = (item) => {
  if (item.internal) {
    return item.status === "پرداخت شده"
      ? statusColors.PAID
      : statusColors.CANCELED;
  }
  const status = item.admin_status?.toUpperCase() || "PENDING";
  return statusColors[status] || statusColors.PENDING;
};

const getStatusText = (item) => {
  return item.internal ? item.status : item.status_admin_fa;
};

const WithDrawCard = ({ item }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isNegative = item.amount < 0;
  const initialStaleTime = calculateWithdrawStaleTime(item.created_at);
  const [staleTime, setStaleTime] = useState(initialStaleTime);

  const minutes = String(Math.floor(Math.abs(staleTime) / 60)).padStart(2, "0");
  const seconds = String(Math.abs(staleTime) % 60).padStart(2, "0");

  useEffect(() => {
    if (staleTime <= 0) return;
    const timer = setTimeout(() => setStaleTime((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [staleTime]);

  const handleReceive = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await RequestConfig.get(
        `withdraws/recipe/url/${item.id}/`
      );
      if ([200, 201].includes(response.status)) {
        window.open(response.data.file_url, "_blank");
      }
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const handleCancelWithdraw = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await RequestConfig.patch(
        `withdraws/cancel/${item.id}/`
      );
      if ([200, 201].includes(response.status)) {
        toast.success("عملیات موفق");
        queryClient.invalidateQueries({ queryKey: ["getWithdraws"] });
      }
    } catch (err) {
      console.error("Cancel error:", err);
    }
  };

  const handleNavigate = () => {
    navigate(`/withdraw-detail/${item.id}`, {
      state: { internal: item.internal },
    });
  };

  const displayTitle = item.internal ? item.recipient : item.destination_name;
  const displaySubtitle = item.internal ? "داخلی" : item.destination_iban;
  const displayStatus = getStatusText(item);
  const statusStyle = getStatusStyle(item);
  const status = item.admin_status?.toUpperCase() || "PENDING";

  return (
    <div className="card-box-wrapper cursor-pointer" onClick={handleNavigate}>
      {/* Header */}
      <div className="flex justify-between items-center w-full">
        <div className="flex flex-col items-start font-bold text-dark p-2 rounded-md">
          <p
            className="max-w-[200px] truncate text-[16px] font-semibold"
            title={displayTitle}
          >
            {displayTitle}
          </p>
          <p className="text-[10px] text-neutral-60">{displaySubtitle}</p>
        </div>

        <div className="flex items-center font-bold text-[16px] gap-2">
          <div>
            {isNegative && <span>-</span>}
            <NumericFormat
              value={Math.abs(item.amount)}
              displayType="text"
              thousandSeparator
            />
          </div>
          <span className="card-box-wrapper-date-text">ریال</span>
        </div>
      </div>

      {/* Status & Download */}
      <div className="flex justify-between items-center mt-2">
        <p className="card-box-wrapper-date-text" dir="ltr">
          {convertToJalali(item.created_at)}
        </p>

        <button
          onClick={handleReceive}
          className="flex gap-2 items-center pr-2 w-fit mr-auto"
        >
          <div
            className={`px-[10px] py-1 rounded-md text-[10px] md:text-[14px] shadow-md font-medium ${statusStyle}`}
          >
            {displayStatus}
          </div>
          <img
            src="/icons/download-square.svg"
            alt="دانلود"
            width={34}
            height={34}
          />
        </button>
      </div>

      {/* Cancel button with countdown */}
      {initialStaleTime > 0 && status !== "CANCELED" && !item.internal && (
        <button
          onClick={handleCancelWithdraw}
          className="btn-exit w-fit mr-auto gap-2 mt-2"
        >
          <p>لغو پرداخت</p>
          <p>{`${minutes}:${seconds}`}</p>
        </button>
      )}
    </div>
  );
};

export default WithDrawCard;
